import { useState, useEffect } from 'react';
import { Menu, X } from 'lucide-react';
import caIndia from '../assets/ca-india.png';
import icaew from '../assets/icaew.png';
import whatsappLogo from '../assets/whatsapp-logo.svg';

const CALENDAR_URL = 'https://calendar.app.google/DtBNRYcoX9a3oD5a9';
const WHATSAPP_URL =
	'https://wa.me/919351275556?text=Hi%2C%20I%27d%20like%20to%20enquire%20about%20your%20India-UK%20tax%20advisory%20services.';

const navLinks = [
	{ label: 'Home', id: null },
	{ label: 'Services', id: 'services' },
	{ label: 'About', id: 'about' },
	{ label: 'Contact', id: 'contact' },
];

const Navigation = () => {
	const [scrolled, setScrolled] = useState(false);
	const [menuOpen, setMenuOpen] = useState(false);

	useEffect(() => {
		const onScroll = () => setScrolled(window.scrollY > 50);
		window.addEventListener('scroll', onScroll);
		return () => window.removeEventListener('scroll', onScroll);
	}, []);

	const scrollTo = (id) => {
		if (id) {
			document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
		} else {
			window.scrollTo({ top: 0, behavior: 'smooth' });
		}
		setMenuOpen(false);
	};

	return (
		<nav
			data-testid='navigation'
			aria-label='Main navigation'
			className='fixed top-0 left-0 right-0 z-50 bg-white transition-all duration-300'
			style={{ boxShadow: scrolled ? '0 2px 20px rgba(0,0,0,0.08)' : 'none' }}>
			<div
				className='max-w-6xl mx-auto px-6 flex items-center justify-between transition-all duration-300'
				style={{ height: scrolled ? '60px' : '72px' }}>
				{/* Logo */}
				<button
					data-testid='nav-logo'
					onClick={() => scrollTo(null)}
					className='flex items-center gap-3'>
					{/* Firm name + tagline */}
					<div className='text-left'>
						<div
							className='font-bold text-lg leading-tight'
							style={{ color: '#2D5A8C', fontFamily: "'Outfit', sans-serif" }}>
							Aditya Ghiya & Associates
						</div>
						<div
							className='font-semibold tracking-widest uppercase'
							style={{ color: '#4B5563', fontSize: '0.625rem' }}>
							India · UK Tax Specialists
						</div>
					</div>

					{/* Divider */}
					<div
						className='hidden sm:block w-px self-stretch mx-1'
						style={{ backgroundColor: '#D1D5DB' }}
					/>

					{/* Credential logos */}
					<div className='hidden sm:flex items-center gap-3'>
						<img
							src={caIndia}
							alt='Institute of Chartered Accountants of India (ICAI) certified'
							className='h-10 w-auto object-contain'
							loading='lazy'
							decoding='async'
							width='40'
							height='40'
						/>
						<img
							src={icaew}
							alt='ICAEW Chartered Accountant'
							loading='lazy'
							decoding='async'
							width='90'
							height='40'
							style={{
								height: '40px',
								width: '90px',
								objectFit: 'cover',
								objectPosition: 'left center',
							}}
						/>
					</div>
				</button>

				{/* Desktop Links */}
				<div className='hidden md:flex items-center gap-8'>
					{navLinks.map(({ label, id }) => (
						<button
							key={label}
							data-testid={`nav-link-${label.toLowerCase()}`}
							onClick={() => scrollTo(id)}
							className='text-sm font-medium transition-colors duration-200 relative group'
							style={{ color: '#6B7280', fontFamily: "'Outfit', sans-serif" }}
							onMouseEnter={(e) => (e.currentTarget.style.color = '#2D5A8C')}
							onMouseLeave={(e) => (e.currentTarget.style.color = '#6B7280')}>
							{label}
							<span
								className='absolute -bottom-1 left-0 w-0 h-0.5 transition-all duration-300 group-hover:w-full'
								style={{ backgroundColor: '#2D5A8C' }}
							/>
						</button>
					))}
				</div>

				{/* Desktop CTAs */}
				<div className='hidden md:flex items-center gap-2'>
					<a
						data-testid='nav-whatsapp-button'
						href={WHATSAPP_URL}
						target='_blank'
						rel='noopener noreferrer'
						aria-label='Chat on WhatsApp'
						className='inline-flex items-center justify-center w-10 h-10 rounded-lg text-white transition-all duration-200 hover:opacity-90 active:scale-95'
						style={{ backgroundColor: '#25D366' }}>
						<img src={whatsappLogo} alt='WhatsApp' className='w-7 h-7' />
					</a>
					<a
						data-testid='nav-cta-button'
						href={CALENDAR_URL}
						target='_blank'
						rel='noopener noreferrer'
						className='inline-flex items-center px-5 py-2.5 rounded-lg text-sm font-semibold text-white transition-all duration-200 hover:opacity-90 hover:shadow-md active:scale-95'
						style={{ backgroundColor: '#F59E0B' }}>
						Book a Consultation
					</a>
				</div>

				{/* Mobile Toggle */}
				<button
					data-testid='mobile-menu-toggle'
					className='md:hidden p-2 rounded-lg'
					style={{ color: '#1F2937' }}
					onClick={() => setMenuOpen(!menuOpen)}>
					{menuOpen ? <X size={20} /> : <Menu size={20} />}
				</button>
			</div>

			{/* Mobile Menu */}
			{menuOpen && (
				<div
					data-testid='mobile-menu'
					className='md:hidden bg-white border-t px-6 py-4 shadow-lg'
					style={{ borderColor: '#F3F4F6' }}>
					<div className='flex flex-col gap-1 mb-4'>
						{navLinks.map(({ label, id }) => (
							<button
								key={label}
								onClick={() => scrollTo(id)}
								className='text-left py-3 text-sm font-medium border-b last:border-0'
								style={{ color: '#1F2937', borderColor: '#F9FAFB' }}>
								{label}
							</button>
						))}
					</div>
					<a
						href={CALENDAR_URL}
						target='_blank'
						rel='noopener noreferrer'
						className='block py-3 rounded-xl text-center text-sm font-semibold text-white mb-2'
						style={{ backgroundColor: '#F59E0B' }}>
						Book a Consultation
					</a>
					<a
						href={WHATSAPP_URL}
						target='_blank'
						rel='noopener noreferrer'
						className='flex items-center justify-center gap-2 py-3 rounded-xl text-center text-sm font-semibold text-white'
						style={{ backgroundColor: '#25D366' }}>
						<img src={whatsappLogo} alt='' className='w-6 h-6' />
						Chat on WhatsApp
					</a>
				</div>
			)}
		</nav>
	);
};

export default Navigation;
